import { Col, Row, message } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  assignToGroups,
  createDocumentGroup,
  fetchChildDocuments,
  fetchGroupById,
  removeFromGroups,
  searchGroupByName,
  updateDocumentGroup,
} from '../../../api/groups';
import { EditGroupForm } from '../../../components';

import { FormattedMessage, useIntl } from 'react-intl';
import { updateUser } from '../../../api/users';
import {
  DocsPayload,
  GroupDetails,
  GroupDetailsWithNewOrDeleteUser,
  UserDetails,
} from '../../../constants/types';
import { CustomError } from '../../../utils/customError';
import { getParentOrigin } from '../../../utils/getKeycloakConfig';
import { Title } from '../../styles';

const EditUserGroup: FC = () => {
  const intl = useIntl();
  const [group, setGroup] = useState<GroupDetails>({} as GroupDetails);
  const [loading, setLoading] = useState(false);
  const { groupId: routeGroupId, groupName } = useParams<{
    groupId: string;
    groupName: any;
  }>();

  const [groupId, setGroupId] = useState<string>('');
  const history = useHistory();
  const location = useLocation();
  const parentOrigin = getParentOrigin();

  useEffect(() => {
    if (groupName && groupName !== 'undefined') {
      searchGroupByName(groupName)
        .then((res: any) => {
          if (res && res[0]) {
            const { id } = res[0];
            setGroupId(id);
          }
        })
        .catch(error => {
          console.error('error', error);
        });
    } else {
      setGroupId(routeGroupId);
    }
  }, [routeGroupId, groupName]);

  useEffect(() => {
    let groupName = '';
    if (groupId) {
      fetchGroupById(groupId)
        .then(data => {
          groupName = data?.name;
          return fetchChildDocuments(
            '/user-management/groups',
            groupName.trim(),
          );
        })
        .then(document => {
          if (document?.data && document.data.length > 0) {
            const data = document.data[0];
            if (data) {
              const { id, otherString1 } = data;
              setGroup(prevGroup => ({
                ...prevGroup,
                name: groupName,
                id,
                description: otherString1,
              }));
            } else {
              setGroup({ name: groupName });
            }
          } else {
            setGroup({ name: groupName });
          }
        })
        .catch((error: any) => {
          message.error(error?.data?.error ?? error?.data?.errors[0]);
        });
    }
  }, [groupId]);

  const backToGroupsPage = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/usergroups`,
        },
        parentOrigin,
      );
    } else {
      history.push('/groups');
    }
  };

  const onSubmit = async (groupDetails: GroupDetailsWithNewOrDeleteUser) => {
    try {
      setLoading(true);
      const { name, description, addedUser, deleteUser } = groupDetails;
      const docs: DocsPayload = {
        name1: name,
        otherString1: description!,
        dataJson: {
          groupName: name,
          groupDescription: description!,
        },
      };

      delete groupDetails.addedUser;
      delete groupDetails.deleteUser;

      if (group.id) {
        try {
          await updateDocumentGroup(group.id, docs);
        } catch (error: any) {
          throw new CustomError(error?.data?.Data?.Error?.errors[0]);
        }
      } else {
        docs.privacyLevel = 'Tenant';
        docs.path = `/user-management/groups/${name
          ?.replace(/ /g, '-')
          .trim()}`;

        try {
          await createDocumentGroup(docs.path, docs);
        } catch (error: any) {
          throw new CustomError(
            error?.data?.Data?.Error?.errors[0] ?? error?.data?.errorCode,
          );
        }
      }
      await addOrDeleteUserToGroup(addedUser!, deleteUser!, name);
      message.success(
        intl.formatMessage(
          {
            id: 'UserGroup.successfullyUpdated',
          },
          {
            usergroup: name,
          },
        ),
        2,
      );
      if (parentOrigin && window.top && window.top !== window) {
        const params = new URLSearchParams(location.search);
        const from = params.get('from');
        if (from && from === 'configuration') {
          window.top.postMessage(
            {
              type: 'redirect',
              url: `/admin/tenant-configuration/features?from=add-group-success`,
            },
            parentOrigin,
          );
        } else {
          window.top.postMessage(
            {
              type: 'redirect',
              url: `/admin/usergroups?updatedGroup=${groupId}`,
            },
            parentOrigin,
          );
        }
      } else {
        history.push(`/groups?updatedGroup=${groupId}`);
      }
    } catch (error: any) {
      if (error instanceof CustomError) {
        message.error(error.message);
      } else {
        message.error(error?.data?.errors[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  const addOrDeleteUserToGroup = async (
    added: {
      account_end_date?: string | undefined;
      id: string;
      user_details?: UserDetails;
    }[],
    deleted: string[],
    groupName: string,
  ) => {
    try {
      const addPromises = added.map(
        async ({ id, account_end_date, user_details }) => {
          await assignToGroups(id, groupId);
          if (groupName === 'tenant-admin' && account_end_date) {
            await updateUser(id, {
              ...(user_details || {}),
              attributes: {
                ...(user_details?.attributes || {}),
                account_end_date: null,
              },
            });
          }
        },
      );

      const deletePromises = deleted.map(async (userId: string) => {
        await removeFromGroups(userId, groupId);
      });

      await Promise.all([...addPromises, ...deletePromises]);
    } catch (error: any) {
      message.error(error?.data?.error);
    }
  };

  return (
    <>
      <Row justify="center" style={{ paddingTop: '28px' }}>
        <Col lg={14} md={18} sm={24} style={{ padding: '20px 30px' }}>
          <Title data-testid="edit-group-title">
            <FormattedMessage id="UserGroup.menu.editUserGroup" />
          </Title>

          {group.name && (
            <EditGroupForm
              loading={loading}
              group={group}
              onSubmit={onSubmit}
              cancel={backToGroupsPage}
              groupId={groupId}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default EditUserGroup;
